/* =============================================================================
   Financials — portfolio-wide money
   -----------------------------------------------------------------------------
   The screen's whole job is that a reader arriving before an investor update
   does not have to reconcile anything: the headline figures, the two charts,
   the cash series and the expense ledger all read from the same frozen domain
   data, and the ledger's footer subtotal covers the whole filtered set rather
   than the page on screen.
   ============================================================================= */

import { useMemo } from 'react';
import {
  BarChart,
  InlineAlert,
  LineChart,
  Metric,
  PaginatedTable,
  Panel,
  formatDate,
  formatMoney,
  formatMonth,
} from 'escrowbuild-ui';
import type {
  AggregationSpec,
  DataSource,
  FilterSpec,
  RawCellValue,
  RemoteOption,
  Row,
  SortSpec,
  TableColumn,
  TableQuery,
  TableResult,
  TableSelectSource,
} from 'escrowbuild-ui';

import {
  useExpenses,
  useFixtureVocabulary,
  useT,
  type AppTranslate,
  type AppTranslationKey,
  type FixtureVocabulary,
} from '@/i18n';
import {
  CASH_FLOW,
  PROJECTS,
  findProject,
  projectTotals,
  type Expense,
} from '@/data/domain';

/* -----------------------------------------------------------------------------
   Portfolio totals
   -----------------------------------------------------------------------------
   `domain.ts` carries per-project totals but no portfolio helper, so the four
   headline figures are summed here from the same `projectTotals` the project
   screens use — a portfolio number that disagreed with its own project cards
   would be the first thing an investor noticed.
   -------------------------------------------------------------------------- */
const TOTALS = PROJECTS.reduce(
  (acc, project) => {
    const totals = projectTotals(project.id);
    return {
      contributed: acc.contributed + totals.contributed,
      actual: acc.actual + totals.actual,
      budgeted: acc.budgeted + totals.budgeted,
      available: acc.available + totals.available,
    };
  },
  { contributed: 0, actual: 0, budgeted: 0, available: 0 },
);

const VARIANCE = TOTALS.actual - TOTALS.budgeted;

/* ---------------------------------------------------------------------------
   The table contract is server-side: filtering, sorting, aggregation and
   pagination clamping all happen behind this function, never by slicing an
   array inside the table. Mirrors the design system's own mock source so a
   real API can replace it unchanged.
   ------------------------------------------------------------------------ */

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Aborted'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new Error('Aborted'));
      },
      { once: true },
    );
  });
}

function asNumber(value: RawCellValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function matchesFilter(row: Row, filter: FilterSpec): boolean {
  const cell = row[filter.field];
  const { op, value } = filter;

  switch (op) {
    case 'isNull':
      return cell === null || cell === undefined || cell === '';
    case 'isNotNull':
      return cell !== null && cell !== undefined && cell !== '';
    case 'eq':
      if (typeof cell === 'boolean' || typeof value === 'boolean') return cell === value;
      if (typeof cell === 'number') return asNumber(value as RawCellValue) === cell;
      return String(cell) === String(value);
    case 'ne':
      return !matchesFilter(row, { ...filter, op: 'eq' });
    case 'contains':
      return String(cell ?? '').toLowerCase().includes(String(value ?? '').toLowerCase());
    case 'startsWith':
      return String(cell ?? '').toLowerCase().startsWith(String(value ?? '').toLowerCase());
    case 'endsWith':
      return String(cell ?? '').toLowerCase().endsWith(String(value ?? '').toLowerCase());
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = asNumber(cell);
      const b = asNumber(value as RawCellValue);
      if (a === null || b === null) {
        const sa = String(cell ?? '');
        const sb = String(value ?? '');
        if (op === 'gt') return sa > sb;
        if (op === 'gte') return sa >= sb;
        if (op === 'lt') return sa < sb;
        return sa <= sb;
      }
      if (op === 'gt') return a > b;
      if (op === 'gte') return a >= b;
      if (op === 'lt') return a < b;
      return a <= b;
    }
    case 'between': {
      if (!Array.isArray(value) || value.length !== 2) return true;
      const [lo, hi] = value as [unknown, unknown];
      if (lo === null || lo === undefined || lo === '') {
        const upper = asNumber(hi as RawCellValue);
        return upper === null ? true : (asNumber(cell) ?? -Infinity) <= upper;
      }
      if (hi === null || hi === undefined || hi === '') {
        const lower = asNumber(lo as RawCellValue);
        return lower === null ? true : (asNumber(cell) ?? Infinity) >= lower;
      }
      const cellNum = asNumber(cell);
      const loNum = asNumber(lo as RawCellValue);
      const hiNum = asNumber(hi as RawCellValue);
      if (cellNum !== null && loNum !== null && hiNum !== null) {
        return cellNum >= loNum && cellNum <= hiNum;
      }
      const text = String(cell ?? '');
      return text >= String(lo) && text <= String(hi);
    }
    case 'in':
      if (!Array.isArray(value) || value.length === 0) return true;
      return value.some((member) => String(member) === String(cell));
    default:
      return true;
  }
}

function compareRows(a: Row, b: Row, sort: SortSpec): number {
  const av = a[sort.field] ?? null;
  const bv = b[sort.field] ?? null;
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') result = av - bv;
  else if (typeof av === 'boolean' && typeof bv === 'boolean') result = Number(av) - Number(bv);
  else result = String(av).localeCompare(String(bv), 'en', { numeric: true, sensitivity: 'base' });

  return sort.dir === 'asc' ? result : -result;
}

function aggregate(rows: Row[], spec: AggregationSpec): number | null {
  const values = rows
    .map((row) => row[spec.field])
    .filter((value): value is string | number => typeof value === 'number' || typeof value === 'string');
  if (values.length === 0) return null;
  if (spec.op === 'count') return rows.length;
  if (spec.op === 'countDistinct') return new Set(values.map((value) => String(value))).size;

  const numbers = values
    .map((value) => asNumber(value as RawCellValue))
    .filter((value): value is number => value !== null);
  if (numbers.length === 0) return null;

  const round = (n: number) =>
    spec.precision === undefined ? n : Math.round(n * 10 ** spec.precision) / 10 ** spec.precision;

  switch (spec.op) {
    case 'sum':
      return round(numbers.reduce((total, n) => total + n, 0));
    case 'avg':
      return round(numbers.reduce((total, n) => total + n, 0) / numbers.length);
    case 'min':
      return Math.min(...numbers);
    case 'max':
      return Math.max(...numbers);
    default:
      return null;
  }
}

function runQuery(source: Row[], query: TableQuery): TableResult {
  // A field carries at most one active filter; a later filter on the same field
  // replaces the earlier one rather than compounding.
  const effective = new Map<string, FilterSpec>();
  for (const filter of query.filters) effective.set(filter.field, filter);

  const filtered = source.filter((row) =>
    [...effective.values()].every((filter) => matchesFilter(row, filter)),
  );

  const sortSpec = query.sort;
  const sorted = sortSpec ? [...filtered].sort((a, b) => compareRows(a, b, sortSpec)) : filtered;

  // Aggregations cover the whole filtered set, never the page.
  const aggregations: Record<string, number | null> = {};
  for (const spec of query.aggregations) aggregations[spec.field] = aggregate(filtered, spec);

  const total = sorted.length;
  const lastPage = Math.max(1, Math.ceil(total / query.pageSize));
  const page = Math.min(Math.max(query.page, 1), lastPage);
  const start = (page - 1) * query.pageSize;

  return {
    rows: sorted.slice(start, start + query.pageSize),
    total,
    page,
    pageSize: query.pageSize,
    aggregations,
  };
}

/** The data source the ledger consumes: the same shape a real endpoint would
 *  return, with the latency and the cancellation a network call has. */
function ledgerSource(rows: Row[]): DataSource {
  let requests = 0;
  return async (query, signal) => {
    requests += 1;
    await pause(160 + ((requests * 137) % 200), signal);
    return runQuery(rows, query);
  };
}

/** A `select` filter source over values that exist in this dataset, so the
 *  popover can only ever offer a value that can match. */
function optionsSource(options: RemoteOption[], placeholder: string): TableSelectSource {
  return {
    placeholder,
    minSearchLength: 1,
    search: async (term, signal) => {
      await pause(120, signal);
      const needle = term.trim().toLowerCase();
      const matched =
        needle === ''
          ? options
          : options.filter((option) => option.label.toLowerCase().includes(needle));
      return { options: matched, truncated: false };
    },
  };
}

const projectName = (id: string) => findProject(id)?.name ?? id;

/* The ledger's status and delivery cells carry the fixture's own codes, so the
   words a reader sees come from these maps — and the filter popover reads the
   same ones, which is why they hold KEYS rather than strings: only the point
   the cell and the options are built has a `t`, and a word written here would
   be a second copy to keep in step. */
const EXPENSE_STATUS_LABELS: Record<Expense['status'], AppTranslationKey> = {
  approved: 'financials.expenseStatus.approved',
  'in-review': 'financials.expenseStatus.inReview',
  disputed: 'financials.expenseStatus.disputed',
};

const DELIVERY_LABELS: Record<Expense['delivery'], AppTranslationKey> = {
  delivered: 'financials.delivery.delivered',
  partial: 'financials.delivery.partial',
  pending: 'financials.delivery.pending',
  'not-applicable': 'financials.delivery.notApplicable',
};

/**
 * A cell formatter for an enum code.
 *
 * A code the map has not seen falls through as itself rather than as a blank:
 * an unrecognised token in a cell is a visible defect with an obvious cause,
 * and the popover's options are drawn from the same values, so a row the map
 * has missed is one the filter cannot offer either.
 */
function labelFormatter(
  t: AppTranslate,
  labels: Record<string, AppTranslationKey>,
): (value: RawCellValue) => string {
  return (value) => {
    if (typeof value !== 'string') return '';
    const key = labels[value];
    return key ? t(key) : value;
  };
}

/**
 * Distinct values from the fixture, as remote-search options. The option's
 * `value` stays the raw fixture token, because that is what the filter has to
 * match; only its label is resolved to a word.
 */
const options = (
  values: string[],
  label: (value: string) => string = (value) => value,
): RemoteOption[] =>
  [...new Set(values)]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: label(value) }));

/**
 * The ledger's rows, built per render rather than at module scope.
 *
 * Two of the cells are the record's own words — the phase's name and the
 * description's sentence — so the rows cannot be a module constant: a constant
 * is resolved before any provider exists and would be frozen in the language the
 * module happened to load in.
 *
 * The category cell carries the fixture's own CODE, not its label: a `select`
 * filter matches on the value in the cell, so the code is what the row has to
 * hold and the formatter is what turns it into a word.
 */
function ledgerRows(expenses: Expense[], phaseName: (phaseId: string) => string): Row[] {
  return expenses.map((expense) => ({
    id: expense.id,
    project: projectName(expense.projectId),
    phase: phaseName(expense.phaseId),
    category: expense.category,
    vendor: expense.vendorName,
    status: expense.status,
    delivery: expense.delivery,
    date: expense.date,
    amount: expense.amount,
    forecast: expense.forecast,
    variance: expense.amount - expense.forecast,
    description: expense.description,
  }));
}

/** The filter popovers, built per locale: their placeholders and the labels
 *  their options offer are both interface text, while each option's VALUE stays
 *  the fixture's own token — which is what the filter has to match. */
function buildSelectSources(
  t: AppTranslate,
  expenses: Expense[],
  vocabulary: FixtureVocabulary,
): Record<string, TableSelectSource> {
  return {
    project: optionsSource(options(expenses.map((expense) => projectName(expense.projectId))), t('financials.search.developments')),
    phase: optionsSource(options(expenses.map((expense) => vocabulary.phase(expense.phaseId))), t('financials.search.phases')),
    category: optionsSource(
      options(expenses.map((expense) => expense.category), vocabulary.expenseCategory),
      t('financials.search.categories'),
    ),
    vendor: optionsSource(options(expenses.map((expense) => expense.vendorName)), t('financials.search.vendors')),
    status: optionsSource(
      options(expenses.map((expense) => expense.status), labelFormatter(t, EXPENSE_STATUS_LABELS)),
      t('financials.search.statuses'),
    ),
    delivery: optionsSource(
      options(expenses.map((expense) => expense.delivery), labelFormatter(t, DELIVERY_LABELS)),
      t('financials.search.deliveryStates'),
    ),
  };
}

const asMoney = (value: RawCellValue): string =>
  typeof value === 'number' ? formatMoney(value, { precision: 0 }) : '';
const asDay = (value: RawCellValue): string =>
  typeof value === 'string' ? formatDate(value, { style: 'medium' }) : '';
/**
 * The column model, built from `t` rather than held at module scope: a header
 * is interface text and a status cell is a code, so only a render can resolve
 * either. The component memoises the result on `t`, which is what keeps the
 * array identity the table memoises its own model on.
 *
 * `Ref` is first and narrow because it is the scan column; `Variance` sits
 * beside `Amount` and `Forecast` so the three money figures that reconcile the
 * forecast are read left to right.
 */
function ledgerColumns(t: AppTranslate, vocabulary: FixtureVocabulary): TableColumn[] {
  const asStatus = labelFormatter(t, EXPENSE_STATUS_LABELS);
  const asDelivery = labelFormatter(t, DELIVERY_LABELS);
  const asCategory = (value: RawCellValue): string =>
    typeof value === 'string' ? vocabulary.expenseCategory(value) : '';

  return [
    { key: 'id', header: t('financials.column.ref'), width: 112, sortable: true, filterable: true, filterType: 'text' },
    { key: 'project', header: t('financials.column.development'), width: 150, sortable: true, filterable: true, filterType: 'select' },
    { key: 'phase', header: t('financials.column.phase'), width: 200, sortable: true, filterable: true, filterType: 'select' },
    { key: 'category', header: t('financials.column.category'), width: 170, sortable: true, filterable: true, filterType: 'select', formatter: asCategory },
    { key: 'vendor', header: t('financials.column.vendor'), width: 200, sortable: true, filterable: true, filterType: 'select' },
    { key: 'status', header: t('common.status'), width: 124, sortable: true, filterable: true, filterType: 'select', formatter: asStatus },
    { key: 'date', header: t('common.date'), width: 142, sortable: true, filterable: true, filterType: 'dateRange', formatter: asDay },
    {
      key: 'amount',
      header: t('common.amount'),
      width: 150,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    {
      key: 'forecast',
      header: t('financials.column.forecast'),
      width: 150,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    {
      key: 'variance',
      header: t('financials.column.variance'),
      width: 150,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    { key: 'delivery', header: t('financials.column.delivery'), width: 132, sortable: true, filterable: true, filterType: 'select', formatter: asDelivery },
    { key: 'description', header: t('financials.column.description'), width: 300, sortable: true, filterable: true, filterType: 'text' },
  ];
}

/* CASH_FLOW stores each month's movement only, so the cumulative position is
   derived here rather than read from the fixture. */
const CASH_ROWS = (() => {
  let cumulative = 0;
  return CASH_FLOW.map((row) => {
    const net = row.contributed - row.spent;
    cumulative += net;
    return { month: row.month, contributed: row.contributed, spent: row.spent, net, cumulative };
  });
})();

const CASH_TOTALS = CASH_ROWS.reduce(
  (acc, row) => ({
    contributed: acc.contributed + row.contributed,
    spent: acc.spent + row.spent,
    net: acc.net + row.net,
    cumulative: row.cumulative,
  }),
  { contributed: 0, spent: 0, net: 0, cumulative: 0 },
);

const HEAD_CLASS =
  'px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const NUM_CELL_CLASS = 'px-3 py-2 text-right tabular-nums';

export function FinancialsPage() {
  const t = useT();

  /*
     The ledger, resolved for the active locale, and the three identities built
     from it. All of them are memoised on values that are stable for the life of
     a locale — `t` included — which is what keeps the table's effect from seeing
     a new source on every render.
  */
  const expenses = useExpenses();
  const vocabulary = useFixtureVocabulary();

  const columns = useMemo(() => ledgerColumns(t, vocabulary), [t, vocabulary]);
  const selectSources = useMemo(
    () => buildSelectSources(t, expenses, vocabulary),
    [t, expenses, vocabulary],
  );
  /** A stable identity — the table keys its fetch on the query, not this. */
  const source = useMemo(
    () => ledgerSource(ledgerRows(expenses, vocabulary.phase)),
    [expenses, vocabulary],
  );

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('financials.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">{t('financials.subtitle')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('financials.metrics.contributed')}
          value={TOTALS.contributed}
          format="money"
          hint={t('financials.metrics.contributedHint')}
        />
        <Metric
          label={t('financials.metrics.spent')}
          value={TOTALS.actual}
          format="money"
          hint={t('financials.metrics.spentHint')}
        />
        <Metric
          label={t('financials.metrics.unspent')}
          value={TOTALS.available}
          format="money"
          tone="info"
          hint={t('financials.metrics.unspentHint')}
        />
        <Metric
          label={t('financials.metrics.variance')}
          value={VARIANCE}
          format="money"
          tone={VARIANCE <= 0 ? 'success' : 'danger'}
          // The delta is the share of the phased budget, not the same figure
          // again. This tile deliberately keeps the whole-life basis, and its
          // hint says so — the portfolio-wide view is where that basis is the
          // honest one, because it is comparing like with like across projects
          // at different stages.
          delta={TOTALS.budgeted === 0 ? 0 : (VARIANCE / TOTALS.budgeted) * 100}
          deltaFormat="percentage"
          trend={VARIANCE <= 0 ? 'down' : 'up'}
          hint={t('financials.metrics.varianceHint')}
        />
      </div>

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold uppercase tracking-label text-heading">
            {t('financials.chart.title')}
          </h2>
          <p className="mt-1 text-sm text-muted">{t('financials.chart.description')}</p>
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          <LineChart />
          <BarChart />
        </div>
      </section>

      <Panel title={t('financials.cash.title')} description={t('financials.cash.description')}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="mb-2 text-left text-xs text-muted">
              {t('financials.cash.caption')}
            </caption>
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className={`${HEAD_CLASS} text-left`}>
                  {t('financials.cash.month')}
                </th>
                <th scope="col" className={`${HEAD_CLASS} text-right`}>
                  {t('financials.cash.contributed')}
                </th>
                <th scope="col" className={`${HEAD_CLASS} text-right`}>
                  {t('financials.cash.spent')}
                </th>
                <th scope="col" className={`${HEAD_CLASS} text-right`}>
                  {t('financials.cash.net')}
                </th>
                <th scope="col" className={`${HEAD_CLASS} text-right`}>
                  {t('financials.cash.cumulative')}
                </th>
              </tr>
            </thead>
            <tbody>
              {CASH_ROWS.map((row) => (
                <tr key={row.month} className="border-b border-line-subtle">
                  <th scope="row" className="px-3 py-2 text-left font-normal text-heading">
                    {formatMonth(row.month)}
                  </th>
                  <td className={`${NUM_CELL_CLASS} text-body`}>
                    {formatMoney(row.contributed, { precision: 0 })}
                  </td>
                  <td className={`${NUM_CELL_CLASS} text-body`}>
                    {formatMoney(row.spent, { precision: 0 })}
                  </td>
                  <td className={`${NUM_CELL_CLASS} text-body`}>
                    {formatMoney(row.net, { precision: 0 })}
                  </td>
                  <td className={`${NUM_CELL_CLASS} text-heading`}>
                    {formatMoney(row.cumulative, { precision: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line-strong">
                <th scope="row" className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-muted">
                  {t('common.total')}
                </th>
                <td className={`${NUM_CELL_CLASS} font-medium text-heading`}>
                  {formatMoney(CASH_TOTALS.contributed, { precision: 0 })}
                </td>
                <td className={`${NUM_CELL_CLASS} font-medium text-heading`}>
                  {formatMoney(CASH_TOTALS.spent, { precision: 0 })}
                </td>
                <td className={`${NUM_CELL_CLASS} font-medium text-heading`}>
                  {formatMoney(CASH_TOTALS.net, { precision: 0 })}
                </td>
                <td className={`${NUM_CELL_CLASS} font-medium text-heading`}>
                  {formatMoney(CASH_TOTALS.cumulative, { precision: 0 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold uppercase tracking-label text-heading">
            {t('financials.ledger.title')}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {t('financials.ledger.intro', { count: expenses.length })}
          </p>
        </div>

        <InlineAlert tone="info" title={t('financials.variance.title')}>
          <p>{t('financials.variance.body')}</p>
        </InlineAlert>

        <PaginatedTable
          id="portfolio-expenses"
          caption={t('financials.ledger.caption')}
          columns={columns}
          dataSource={source}
          rowKey="id"
          selectSources={selectSources}
          pageSizeOptions={[10, 25, 50]}
          defaultPageSize={10}
          emptyTitle={t('financials.ledger.emptyTitle')}
          emptyDescription={t('financials.ledger.emptyDescription')}
          errorTitle={t('financials.ledger.errorTitle')}
        />
      </section>
    </div>
  );
}
